import React, { useState, useEffect } from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Alert,
    CircularProgress,
    Grid,
    Box,
    IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkIcon from '@mui/icons-material/Link';
import { useTranslation, TFunction } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ActiveSuspension, HistorySuspension } from '../../types';
import { getActiveSuspensions, getHistorySuspensions, getCarsharingClient } from '../../api/service/customerbaseApi';
import { GridColDef } from '@mui/x-data-grid';
import CarSharingDataGrid from './CarSharingDataGrid';
import { CarsharingRedirectParams, redirectToCarsharing } from '../../../../util/carsharingService';
import { useCarSharingContext } from './CarSharingContext';

const dateValueFormatter = ({ value }: { value: string | undefined | null }) => {
    if (!value) return '-';

    const date = new Date(value);
    if (isNaN(date.getTime())) return '-';

    return date.toLocaleString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'Europe/Rome',
    }).replace(',', '');
};

const getActiveColumns = (t: TFunction): GridColDef[] => [
  {
    field: 'creationDate',
    headerName: t('csSuspensions.headers.creationDate'),
    flex: 1,
    minWidth: 150,
    valueFormatter: dateValueFormatter,
  },
  {
    field: 'suspendedDate',
    headerName: t('csSuspensions.headers.suspendedDate'),
    flex: 1,
    minWidth: 120,
    valueFormatter: dateValueFormatter,
  },
  {
    field: 'suspendedStatus',
    headerName: t('csSuspensions.headers.suspendedStatus'),
    flex: 1,
    minWidth: 100,
  },
  {
    field: 'suspendedTypeDesc',
    headerName: t('csSuspensions.headers.suspendedTypeDesc'),
    flex: 1,
    minWidth: 200,
  },
  {
    field: 'note',
    headerName: t('csSuspensions.headers.note'),
    flex: 1,
    minWidth: 200,
  },
  {
    field: 'assistentId',
    headerName: t('csSuspensions.headers.assistentId'),
    flex: 1,
    minWidth: 180,
  },
];

const getHistoryColumns = (t: TFunction): GridColDef[] => [
  {
    field: 'creationDate',
    headerName: t('csSuspensions.headers.creationDate'),
    width: 150,
    valueFormatter: dateValueFormatter,
  },
  {
    field: 'suspendedDate',
    headerName: t('csSuspensions.headers.suspendedDate'),
    width: 120,
    valueFormatter: dateValueFormatter,
  },
  {
    field: 'suspendedStatus',
    headerName: t('csSuspensions.headers.suspendedStatus'),
    width: 100,
  },
  {
    field: 'suspendedTypeDesc',
    headerName: t('csSuspensions.headers.suspendedTypeDesc'),
    width: 200,
  },
  {
    field: 'note',
    headerName: t('csSuspensions.headers.note'),
    flex: 1,
    minWidth: 200,
  },
  {
    field: 'assistentId',
    headerName: t('csSuspensions.headers.assistentId'),
    width: 180,
  },
  {
    field: 'historyNote',
    headerName: t('csSuspensions.headers.historyNote'),
    flex: 1,
    minWidth: 200,
  },
  {
    field: 'historyAssistentId',
    headerName: t('csSuspensions.headers.historyAssistentId'),
    width: 170,
  },
  {
    field: 'historyDate',
    headerName: t('csSuspensions.headers.historyDate'),
    width: 120,
    valueFormatter: dateValueFormatter,
  },
];

const languageIdToStringMap: Record<number, string> = {
  10002: 'it',
  10001: 'en',
  10004: 'fr',
};

export default function CustomerBaseCarSharingSuspensions() {
    const { t } = useTranslation('customerbase');
    const { personId, contractId, csCustomerId, language } = useCarSharingContext();
    const [expanded, setExpanded] = useState(false);
    const [hasBeenOpened, setHasBeenOpened] = useState(false);

    useEffect(() => {
        if (expanded && !hasBeenOpened) setHasBeenOpened(true);
    }, [expanded, hasBeenOpened]);

    const handleRedirect = async () => {
        if (!csCustomerId) return;

        const params: CarsharingRedirectParams = {
            lang: language || 'it',
            type: 'CUSTOMER',
            section: 'SUSPENSIONS',
            id: String(csCustomerId),
        };

    try {
      await redirectToCarsharing(params);
    } catch (error) {
      console.error('Redirect failed:', error);
    }
  };

    const {
        data: activeSuspensions,
        isError: isErrorActive,
        error: errorActive,
        isLoading: isLoadingActive,
    } = useQuery<ActiveSuspension[]>(
        ['activeSuspensions', personId, contractId, language],
        () => getActiveSuspensions(personId!, Number(contractId), getCarsharingClient()),
        {
            enabled: hasBeenOpened && !!personId && !!contractId,
        }
    );

    const {
        data: historySuspensions,
        isError: isErrorHistory,
        error: errorHistory,
        isLoading: isLoadingHistory,
    } = useQuery<HistorySuspension[]>(
        ['historySuspensions', personId, contractId, language],
        () => getHistorySuspensions(personId!, Number(contractId), getCarsharingClient()),
        {
            enabled: hasBeenOpened && !!personId && !!contractId,
            retry: 1,
        }
    );

    const activeColumns = getActiveColumns(t);
    const historyColumns = getHistoryColumns(t);

    const activeSuspensionsWithId = (activeSuspensions || []).map((s) => ({
        ...s,
        id: s.creationDate + s.suspendedType,
    }));

    const historySuspensionsWithId = (historySuspensions || []).map((s) => ({
        ...s,
        id: s.creationDate + s.suspendedType,
    }));

    return (
        <Accordion expanded={expanded} onChange={(_, isExpanded) => setExpanded(isExpanded)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <Typography sx={{ fontWeight: 500 }}>{t('tabs.accordionsLabels.csSuspensions')}</Typography>
                    {expanded && (
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                void handleRedirect();
                            }}
                            disabled={!csCustomerId}
                            color="secondary"
                        >
                            <LinkIcon />
                        </IconButton>
                    )}
                </Box>
            </AccordionSummary>

            <AccordionDetails>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        {isLoadingActive ? (
                            <CircularProgress />
                        ) : isErrorActive ? (
                            <Alert severity="error">{(errorActive as Error)?.message}</Alert>
                        ) : (
                            <CarSharingDataGrid
                                title={t('csSuspensions.active')}
                                rows={activeSuspensionsWithId}
                                columns={activeColumns}
                            />
                        )}
                    </Grid>

                    <Grid item xs={12}>
                        {isLoadingHistory ? (
                            <CircularProgress />
                        ) : isErrorHistory ? (
                            <Alert severity="error">{(errorHistory as Error)?.message}</Alert>
                        ) : (
                            <CarSharingDataGrid
                                title={t('csSuspensions.history')}
                                rows={historySuspensionsWithId}
                                columns={historyColumns}
                            />
                        )}
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
}